import { useSyncExternalStore } from "react";

/**
 * Global store for the 4-tier capabilities quota interception and Upgrade Modal.
 *
 * Backed by useSyncExternalStore for tear-free, decoupled updates from outside
 * React (such as Axios HTTP interceptors and native fetch SSE error handlers).
 */

const INITIAL_STATE = Object.freeze({
  isOpen: false,
  data: null,
});

let state = INITIAL_STATE;
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

/**
 * Subscribes a listener function to store updates.
 *
 * @param {() => void} listener
 * @returns {() => void} unsubscribe callback
 */
export function subscribeUpgradeModal(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Returns the current immutable state snapshot.
 *
 * @returns {{ isOpen: boolean, data: any }}
 */
export function getUpgradeModalSnapshot() {
  return state;
}

/**
 * Opens the Upgrade Modal with contextual quota and tier upgrade information.
 *
 * @param {object} [data] - Quota details (quota_type, current_usage, limit, upgrade_tier, message)
 */
export function openUpgradeModal(data = {}) {
  // Normalize payload if nested under error response or data
  const payload = data?.data || data || {};
  state = Object.freeze({
    isOpen: true,
    data: payload,
  });
  emit();
}

/**
 * Closes the Upgrade Modal while preserving or clearing modal state.
 */
export function closeUpgradeModal() {
  if (!state.isOpen) return;
  state = Object.freeze({
    isOpen: false,
    data: state.data,
  });
  emit();
}

/**
 * Resets the Upgrade Modal store to initial state (closed, null data).
 */
export function resetUpgradeModal() {
  state = INITIAL_STATE;
  emit();
}

/**
 * Custom React hook consuming the Upgrade Modal external store.
 *
 * @returns {{ isOpen: boolean, data: any, openUpgradeModal: typeof openUpgradeModal, closeUpgradeModal: typeof closeUpgradeModal }}
 */
export function useUpgradeModal() {
  const current = useSyncExternalStore(
    subscribeUpgradeModal,
    getUpgradeModalSnapshot,
    getUpgradeModalSnapshot
  );

  return {
    isOpen: current.isOpen,
    data: current.data,
    openUpgradeModal,
    closeUpgradeModal,
  };
}
